import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Avatar,
  Grid,
  useTheme,
  alpha,
  Card,
  CardContent
} from '@mui/material';
import {
  Person as PersonIcon,
  PhotoCamera as PhotoCameraIcon,
  Save as SaveIcon,
  AccountCircle as AccountCircleIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { supabase } from '../../../supabaseClient';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    background: 'rgba(255, 255, 255, 1)',
    fontSize: '1rem',
    minHeight: 56,
    border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
    transition: 'all 0.3s ease',
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    },
    '&.Mui-focused': {
      border: `1px solid ${theme.palette.primary.main}`,
      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`
    },
    '&:hover': {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`
    }
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    fontSize: '1rem',
    color: 'rgba(0, 0, 0, 0.6)',
    backgroundColor: 'rgba(255, 255, 255, 1)',
    padding: '0 8px',
    marginLeft: '-8px',
    transition: 'all 0.3s ease',
    '&.Mui-focused': {
      color: theme.palette.primary.main,
      fontWeight: 500,
      backgroundColor: 'rgba(255, 255, 255, 1)'
    },
    '&.MuiInputLabel-shrink': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
      padding: '0 8px'
    }
  }
}));

const ProfileManagement = ({ onProfileChange }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: ''
  });
  const [avatarUrl, setAvatarUrl] = useState('');

  useEffect(() => {
    loadCurrentUser();
  }, []);

  const loadCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUser(user);
        await loadExistingProfile(user.id);
      }
    } catch (err) {
      setError('Failed to load user data');
    }
  };

  const loadExistingProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', userId)
        .single();

      if (error) throw error;
      
      if (data) {
        const profileInfo = {
          first_name: data.first_name || '',
          last_name: data.last_name || ''
        };
        setProfileData(profileInfo);
        if (onProfileChange) {
          onProfileChange(profileInfo);
        }
      }

      await loadAvatarFromStorage(userId);
    } catch (err) {
      setError('Failed to load profile data');
    }
  };

  const loadAvatarFromStorage = async (userId) => {
    try {
      const { data: files, error } = await supabase.storage
        .from('profile-avatars')
        .list(`${userId}/`, {
          limit: 1,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) return;

      if (files && files.length > 0) {
        const fileName = `${userId}/${files[0].name}`;
        const { data, error: signedUrlError } = await supabase.storage
          .from('profile-avatars')
          .createSignedUrl(fileName, 3600);
        
        if (signedUrlError) return;
        
        setAvatarUrl(data.signedUrl);
      } else {
        setAvatarUrl('');
      }
    } catch (err) {
      // Silent fail
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => {
      const updated = { ...prev, [name]: value };
      if (onProfileChange) {
        onProfileChange(updated);
      }
      return updated;
    });
    
    if (error) setError(null);
    if (success) setSuccess(false);
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !currentUser) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setUploadingAvatar(true);
    setError(null);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${currentUser.id}/avatar.${fileExt}`;

      const { data, error: uploadError } = await supabase.storage
        .from('profile-avatars')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from('profile-avatars')
        .createSignedUrl(fileName, 3600);

      if (signedUrlError) throw signedUrlError;

      setAvatarUrl(signedUrlData.signedUrl);
      setSuccess(true);

      setTimeout(() => {
        loadAvatarFromStorage(currentUser.id);
        setSuccess(false);
      }, 1500);

    } catch (err) {
      setError('Failed to upload avatar: ' + err.message);
    } finally {
      setUploadingAvatar(false);
    }
  };



  const handleRemoveAvatar = async () => {
    if (!currentUser) return;

    setUploadingAvatar(true);
    setError(null);

    try {
      const { data: files } = await supabase.storage
        .from('profile-avatars')
        .list(`${currentUser.id}/`, {
          limit: 10
        });

      if (files && files.length > 0) {
        const filesToRemove = files.map(file => `${currentUser.id}/${file.name}`);
        
        const { error: deleteError } = await supabase.storage
          .from('profile-avatars')
          .remove(filesToRemove);

        if (deleteError) throw deleteError;
      }

      setAvatarUrl('');
      setSuccess(true);
      
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to remove avatar: ' + err.message);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const getInitials = () => {
    const first = profileData.first_name?.charAt(0) || '';
    const last = profileData.last_name?.charAt(0) || '';
    return (first + last).toUpperCase() || currentUser?.email?.charAt(0).toUpperCase() || 'U';
  };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        mb: 4,
        p: 4,
        borderRadius: 4,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: 'white',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
          borderRadius: '50%',
          transform: 'translate(50%, -50%)',
          zIndex: 0
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.2rem',
            mb: 1,
            lineHeight: 1.2
          }}>
            Profile Management
          </Typography>
          
          <Typography variant="body1" sx={{ 
            color: 'text.secondary', 
            fontWeight: 500,
            fontSize: '1rem',
            maxWidth: '400px'
          }}>
            Update your personal information and profile picture
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
          Profile updated successfully!
        </Alert>
      )}

      <Card sx={{ 
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
        mb: 4,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        }
      }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 500, mb: 3 }}>
            Profile Picture
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar 
                sx={{ 
                  width: 100, 
                  height: 100, 
                  bgcolor: theme.palette.grey[100],
                  color: 'text.primary',
                  fontSize: '2rem',
                  fontWeight: 500,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                }}
                src={avatarUrl || undefined}
              >
                {!avatarUrl && (
                  profileData.first_name || profileData.last_name ? 
                    getInitials() : 
                    <AccountCircleIcon sx={{ fontSize: 60, color: alpha(theme.palette.grey[600], 0.6) }} />
                )}
              </Avatar>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="avatar-upload"
                type="file"
                onChange={handleAvatarUpload}
                disabled={uploadingAvatar}
              />
              <label htmlFor="avatar-upload">
                <Button
                  component="span"
                  disabled={uploadingAvatar}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    minWidth: 0,
                    bgcolor: 'white',
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                    '&:hover': { 
                      bgcolor: 'white',
                      transform: 'translateY(-1px) scale(1.05)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                      border: `1px solid ${theme.palette.primary.main}`
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {uploadingAvatar ? (
                    <CircularProgress size={16} sx={{ color: alpha(theme.palette.grey[600], 0.8) }} />
                  ) : (
                    <PhotoCameraIcon sx={{ fontSize: 20, color: alpha(theme.palette.grey[600], 0.8) }} />
                  )}
                </Button>
              </label>
            </Box>
            
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                Update your profile picture
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Upload a new photo to personalize your account
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', mb: 2, display: 'block' }}>
                Recommended: Square image, at least 200x200px, max 5MB
              </Typography>
              {avatarUrl && (
                <Button
                  onClick={handleRemoveAvatar}
                  disabled={uploadingAvatar}
                  size="small"
                  sx={{
                    color: 'error.main',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.error.main, 0.08)
                    }
                  }}
                  startIcon={<DeleteIcon />}
                >
                  Remove Avatar
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ 
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        }
      }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 500, mb: 3 }}>
            Personal Information
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="First Name"
                name="first_name"
                value={profileData.first_name}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={profileData.last_name}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </Grid>
          </Grid>


        </CardContent>
      </Card>
    </Box>
  );
};

export default ProfileManagement;